
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Layout from '../../../../../components/docs/Layout';
import styles from '../../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Permissions Manager API',
  layout: 'guide',
  description: 'Read connected applications, consent activity, and revoke access',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Schema = makeShortcode("Schema");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Permissions Manager API' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "overview"
      }}>{`Overview`}</h2>
      <Callout warning mdxType="Callout">
        <p>{`This page is provided as a reference for data partners already using the Plaid Permissions Manager API. New integrations should instead use the FDX-aligned `}<a parentName="p" {...{
            "href": "/core-exchange/docs/consent-management/api"
          }}>{`Consent API`}</a>{`, which models consent as an FDX consent grant, allowing you to build a single consent management integration serving multiple data access platforms. `}</p>
        <p>{`If you are an existing Permissions Manager user interested in migrating, see `}<a parentName="p" {...{
            "href": "/core-exchange/docs/consent-management/api#migrating-from-permissions-manager-to-the-consent-api"
          }}>{`Migrating from Permissions Manager to the Consent API`}</a>{`. `}</p>
      </Callout>
      <p>{`The Permissions Manager API reports which Plaid-powered applications one of your customers has connected, which accounts and data types each application can read, and when each application last read them. It also lets you revoke an application's access.`}</p>
      <p>{`It is the API behind a consumer-facing consent portal on your domain: the customer sees their connections and disconnects the ones they no longer want, and Plaid enforces that revocation across the ecosystem. Single-institution accounts can read the same data with no code in the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management"
        }}>{`Data Partner Dashboard`}</a>{`, whose Permissions Manager tabs call these same endpoints; this page covers the API, which is available to every integration model, including platforms.`}</p>
      <p>{`For conceptual background on authorization records and the webhooks that accompany these endpoints, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management"
        }}>{`Consent management`}</a>{`.`}</p>
      <h2 {...{
        "id": "authentication"
      }}>{`Authentication`}</h2>
      <p>{`Find your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` on the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/developers/keys"
        }}>{`Developer > Keys`}</a>{` tab of the Data Partner Dashboard. Include both as the `}<inlineCode parentName="p">{`PLAID-CLIENT-ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`PLAID-SECRET`}</inlineCode>{` headers, or as `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` in the request body. All requests must be made over HTTPS.`}</p>
      <h2 {...{
        "id": "customer-identifiers-and-access-tokens"
      }}>{`Customer identifiers and access tokens`}</h2>
      <p>{`Permissions Manager identifies a customer by an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, not by a customer identifier, so every integration starts with `}<a parentName="p" {...{
          "href": "#exchange-a-customer-identifier-for-an-access-token"
        }}><inlineCode parentName="a">{`/item/import`}</inlineCode></a>{`.`}</p>
      <p>{`Use the same persistent, unique identifier you use as the `}<inlineCode parentName="p">{`sub`}</inlineCode>{` claim in your OIDC ID token — see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-server-setup/#unique-user-identifier-consistency-key"
        }}>{`Unique user identifier (consistency key)`}</a>{`. Reusing an identifier across two customers merges their connections; changing it for an existing customer strands the connections recorded under the old one.`}</p>
      <p>{`Because `}<a parentName="p" {...{
          "href": "#exchange-a-customer-identifier-for-an-access-token"
        }}><inlineCode parentName="a">{`/item/import`}</inlineCode></a>{` is idempotent, you can call it lazily the first time a customer opens your portal, batch it ahead of time, or call it again whenever you need the token.`}</p>
      <h3 {...{
        "id": "exchange-a-customer-identifier-for-an-access-token"
      }}>{`Exchange a customer identifier for an access token`}</h3>
      <h4 {...{
        "id": "post-itemimport"
      }}><inlineCode parentName="h4">{`POST /item/import`}</inlineCode></h4>
      <Schema method='POST' route='/item/import' type='request' mdxType="Schema">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST 'https://production.plaid.com/item/import' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": "<client_id>",
    "secret": "<secret>",
    "user_auth": {
      "user_id": "<user_id>"
    }
  }'
`}</code></pre>
      </Schema>
      <Schema method='POST' route='/item/import' type='response' mdxType="Schema" />
      <h2 {...{
        "id": "endpoints"
      }}>{`Endpoints`}</h2>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Endpoint`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Functionality`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#exchange-a-customer-identifier-for-an-access-token"
              }}><inlineCode parentName="a">{`/item/import`}</inlineCode></a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Exchange your customer identifier for an access token`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#list-a-customers-connected-applications"
              }}><inlineCode parentName="a">{`/item/application/list`}</inlineCode></a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`List connected and disconnected applications, with their scopes`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#read-consent-activity-and-last-access-times"
              }}><inlineCode parentName="a">{`/item/activity/list`}</inlineCode></a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Read consent activity history and per-scope last-access times`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#revoke-an-applications-access"
              }}><inlineCode parentName="a">{`/item/application/unlink`}</inlineCode></a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Revoke an application's access to a customer's data`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#testing-in-sandbox"
              }}><inlineCode parentName="a">{`/sandbox/item/application/seed`}</inlineCode></a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Sandbox only: connect a demo application to a customer`}</td>
          </tr>
        </tbody>
      </table>
      <h3 {...{
        "id": "list-a-customers-connected-applications"
      }}>{`List a customer's connected applications`}</h3>
      <h4 {...{
        "id": "post-itemapplicationlist"
      }}><inlineCode parentName="h4">{`POST /item/application/list`}</inlineCode></h4>
      <p>{`Call this each time a customer opens your portal rather than serving a cached copy, so that revocations made elsewhere in the ecosystem are reflected immediately.`}</p>
      <p>{`The response separates `}<inlineCode parentName="p">{`applications`}</inlineCode>{`, which the customer has authorized, from `}<inlineCode parentName="p">{`disconnected_applications`}</inlineCode>{`, which they authorized and later revoked. Render disconnected applications as history: they carry no `}<inlineCode parentName="p">{`scopes`}</inlineCode>{` or `}<inlineCode parentName="p">{`created_at`}</inlineCode>{`, because there is no longer any access to describe.`}</p>
      <p>{`Parse `}<inlineCode parentName="p">{`created_at`}</inlineCode>{` permissively. Depending on when your integration was set up, it arrives either as an ISO 8601 datetime (`}<inlineCode parentName="p">{`2020-01-01T00:00:00Z`}</inlineCode>{`) or as a date alone (`}<inlineCode parentName="p">{`2020-01-01`}</inlineCode>{`).`}</p>
      <Schema method='POST' route='/item/application/list' type='request' mdxType="Schema">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST 'https://production.plaid.com/item/application/list' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": "<client_id>",
    "secret": "<secret>",
    "access_token": "<access_token>"
  }'
`}</code></pre>
      </Schema>
      <Schema method='POST' route='/item/application/list' type='response' mdxType="Schema" />
      <h4 {...{
        "id": "reading-the-scopes-object"
      }}>{`Reading the scopes object`}</h4>
      <p><inlineCode parentName="p">{`scopes.product_access`}</inlineCode>{` maps a data type to whether the customer authorized it. The keys that can appear are:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Key`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Includes`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`account_balance_info`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Account name, type, description, balances, and masked account number`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`contact_info`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Account owner name, email, phone, and address`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`account_routing_number`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Account and routing numbers`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`transactions`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Transaction amounts, dates, descriptions, and categories, and derived insights`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`credit_loan_info`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Balances, payment dates and amounts due, credit limits, rates, and loan terms`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`investments`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Securities details, quantities, prices, and investment transactions`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`bank_statements`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`PDF statements`}</td>
          </tr>
        </tbody>
      </table>
      <p>{`Only the data types an application requested appear, so treat a missing key as "not authorized" rather than as an error. Applications built on Plaid products outside this set can introduce further keys, so parse `}<inlineCode parentName="p">{`product_access`}</inlineCode>{` as a map rather than a fixed struct.`}</p>
      <Callout mdxType="Callout">
        <p>{`If you integrated before June 2024, your `}<inlineCode parentName="p">{`product_access`}</inlineCode>{` keys may use different field names than the ones in the table above. Contact your Plaid solutions engineering team if you'd like to migrate to the new field names.`}</p>
      </Callout>
      <p><inlineCode parentName="p">{`scopes.accounts`}</inlineCode>{` lists the customer's accounts by `}<inlineCode parentName="p">{`unique_id`}</inlineCode>{` — the same account identifier your FDX API returns — each with whether the application may read it. `}<inlineCode parentName="p">{`new_accounts`}</inlineCode>{` reports whether accounts the customer opens later are included automatically. An empty `}<inlineCode parentName="p">{`accounts`}</inlineCode>{` array on a long-standing connection means the customer authorized every available account; those connections predate per-account selection.`}</p>
      <p>{`Where you scope data types per account rather than per connection, each account also carries `}<inlineCode parentName="p">{`account_product_access`}</inlineCode>{`, keyed by the per-account scopes configured for your integration.`}</p>
      <h3 {...{
        "id": "read-consent-activity-and-last-access-times"
      }}>{`Read consent activity and last access times`}</h3>
      <h4 {...{
        "id": "post-itemactivitylist"
      }}><inlineCode parentName="h4">{`POST /item/activity/list`}</inlineCode></h4>
      <p><inlineCode parentName="p">{`/item/activity/list`}</inlineCode>{` is the audit view of your portal: what happened to a customer's connections, and when each application last read their data. Results are paginated: pass `}<inlineCode parentName="p">{`count`}</inlineCode>{` to size a page and the `}<inlineCode parentName="p">{`cursor`}</inlineCode>{` from the previous response to fetch the next one. An omitted `}<inlineCode parentName="p">{`cursor`}</inlineCode>{` in the response means you have reached the end.`}</p>
      <Schema method='POST' route='/item/activity/list' type='request' mdxType="Schema">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST 'https://production.plaid.com/item/activity/list' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": "<client_id>",
    "secret": "<secret>",
    "access_token": "<access_token>"
  }'
`}</code></pre>
      </Schema>
      <p>{`Each entry in `}<inlineCode parentName="p">{`activities`}</inlineCode>{` records one event. `}<inlineCode parentName="p">{`activity`}</inlineCode>{` is the event type, `}<inlineCode parentName="p">{`initiated_date`}</inlineCode>{` is when it happened, `}<inlineCode parentName="p">{`state`}</inlineCode>{` is its outcome, `}<inlineCode parentName="p">{`initiator`}</inlineCode>{` identifies who caused it, and `}<inlineCode parentName="p">{`target_application_id`}</inlineCode>{` names the application it acted on:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`activity`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Recorded when`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ITEM_CREATE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The customer connected an application`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ITEM_IMPORT`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`You called `}<a parentName="td" {...{
                "href": "#exchange-a-customer-identifier-for-an-access-token"
              }}><inlineCode parentName="a">{`/item/import`}</inlineCode></a>{` for the customer`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ITEM_UPDATE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`An existing connection was reauthorized or its scopes changed`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ITEM_UNLINK`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`An application's access ended, including through `}<a parentName="td" {...{
                "href": "#revoke-an-applications-access"
              }}><inlineCode parentName="a">{`/item/application/unlink`}</inlineCode></a></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PORTAL_UNLINK`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The customer disconnected an application in `}<a parentName="td" {...{
                "href": "https://my.plaid.com"
              }}>{`Plaid Portal`}</a></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PORTAL_ITEMS_DELETE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The customer deleted their data in Plaid Portal`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ITEM_REMOVE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The connected application removed the connection on its own side`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SCOPES_UPDATE`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The scopes on a connection were updated`}</td>
          </tr>
        </tbody>
      </table>
      <p><inlineCode parentName="p">{`last_data_access_times`}</inlineCode>{` holds one object per application, keyed by `}<inlineCode parentName="p">{`application_id`}</inlineCode>{`, with a timestamp for every `}<inlineCode parentName="p">{`product_access`}</inlineCode>{` scope except `}<inlineCode parentName="p">{`bank_statements`}</inlineCode>{`. A `}<inlineCode parentName="p">{`null`}</inlineCode>{` timestamp means the application has never read that data type. This is the field to show a customer who wants to know whether an app they authorized months ago is still reading their accounts.`}</p>
      <Callout mdxType="Callout">
        <p>{`If you integrated before June 2024, your `}<inlineCode parentName="p">{`last_data_access_times`}</inlineCode>{` object may use different field names than the ones above, and may also include `}<inlineCode parentName="p">{`payroll_info`}</inlineCode>{` and `}<inlineCode parentName="p">{`transaction_risk_info`}</inlineCode>{`, which are deprecated. Contact your Plaid solutions engineering team if you'd like to migrate to the new field names.`}</p>
      </Callout>
      <h3 {...{
        "id": "revoke-an-applications-access"
      }}>{`Revoke an application's access`}</h3>
      <h4 {...{
        "id": "post-itemapplicationunlink"
      }}><inlineCode parentName="h4">{`POST /item/application/unlink`}</inlineCode></h4>
      <p>{`Call this endpoint as soon as a customer disconnects an application in your portal, one call per application, and never in a batch — Plaid revokes the application's access on receipt, and the delay before that call is time the application can still read data.`}</p>
      <p>{`If you offer users the ability to revoke access, you must keep the ecosystem in sync:`}</p>
      <ol>
        <li parentName="ol"><strong parentName="li">{`User revokes access on your domain:`}</strong>{` The customer visits your consent portal and disconnects an application`}</li>
        <li parentName="ol"><strong parentName="li">{`Call `}<a parentName="strong" {...{
              "href": "#revoke-an-applications-access"
            }}><inlineCode parentName="a">{`/item/application/unlink`}</inlineCode></a>{`:`}</strong>{` Your system calls Plaid's revocation endpoint`}</li>
        <li parentName="ol"><strong parentName="li">{`Plaid notifies the application:`}</strong>{` Plaid sends the application a `}<inlineCode parentName="li">{`USER_PERMISSION_REVOKED`}</inlineCode>{` webhook`}</li>
        <li parentName="ol"><strong parentName="li">{`Plaid severs the connection:`}</strong>{` Plaid blocks the application from accessing data`}</li>
        <li parentName="ol"><strong parentName="li">{`Application stops requesting data:`}</strong>{` The application knows the customer revoked access and stops making requests`}</li>
      </ol>
      <p>{`Without step 2, the application keeps its access and your portal shows a disconnection that never took effect.`}</p>
      <p>{`A customer who wants the application again has to go through your OAuth flow from the start; there is no way to restore a revoked connection.`}</p>
      <Schema method='POST' route='/item/application/unlink' type='request' mdxType="Schema">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST 'https://production.plaid.com/item/application/unlink' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": "<client_id>",
    "secret": "<secret>",
    "access_token": "<access_token>",
    "application_id": "<application_id>"
  }'
`}</code></pre>
      </Schema>
      <Schema method='POST' route='/item/application/unlink' type='response' mdxType="Schema" />
      <h2 {...{
        "id": "webhooks"
      }}>{`Webhooks`}</h2>
      <p>{`Plaid can optionally send webhooks to you when authorization events occur. They are independent of which API you read with. Single-institution accounts can configure them in the Data Partner Dashboard via the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/developers/webhooks"
        }}>{`Developers > Webhooks`}</a>{` page. Platform accounts should reach out to your Plaid contact to have these enabled.`}</p>
      <Image src='/assets/img/core-exchange/permissions-manager/webhooks.png' alt='Webhook configuration interface in the Data Partner Dashboard' caption='Configure webhooks for real-time connection and disconnection alerts' expandable={true} mdxType="Image" />
      <p>{`Plaid sends two webhooks: `}<a parentName="p" {...{
          "href": "#authorization_granted"
        }}><inlineCode parentName="a">{`AUTHORIZATION_GRANTED`}</inlineCode></a>{` and `}<a parentName="p" {...{
          "href": "#consent_revoked"
        }}><inlineCode parentName="a">{`CONSENT_REVOKED`}</inlineCode></a>{`. For the payload shape and full examples, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management/api#webhooks"
        }}>{`Webhooks`}</a>{` on the Consent API reference.`}</p>
      <h3 {...{
        "id": "authorization_granted"
      }}><inlineCode parentName="h3">{`AUTHORIZATION_GRANTED`}</inlineCode></h3>
      <p>{`Notifies you every time a customer connects a new application via Plaid. While your system will already know about new OAuth authorizations, this webhook also alerts you to connections created via the returning user experience, where the customer never goes through your OAuth flow and this webhook is your only real-time signal that a new connection was created.`}</p>
      <p><strong parentName="p">{`When it fires:`}</strong>{` After a customer successfully authorizes a new application.`}</p>
      <p><strong parentName="p">{`Payload includes:`}</strong>{` The customer (`}<inlineCode parentName="p">{`user_identifier`}</inlineCode>{`) and application details (`}<inlineCode parentName="p">{`application_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`application_name`}</inlineCode>{`).`}</p>
      <p><strong parentName="p">{`What to do:`}</strong>{` Record the new connection. The webhook identifies the customer and the application, but not the accounts or data types they authorized, so treat it as a signal to call `}<a parentName="p" {...{
          "href": "#list-a-customers-connected-applications"
        }}><inlineCode parentName="a">{`/item/application/list`}</inlineCode></a>{` for the full set of scopes. If you offer a consent portal, display this new connection so customers can view and manage it.`}</p>
      <h3 {...{
        "id": "consent_revoked"
      }}><inlineCode parentName="h3">{`CONSENT_REVOKED`}</inlineCode></h3>
      <p>{`Notifies you when a customer revokes an application's access, either through the application itself or via `}<a parentName="p" {...{
          "href": "https://my.plaid.com"
        }}>{`my.plaid.com`}</a>{` (Plaid Portal). If you offer a consent portal, you need this webhook to catch the revocations that happen elsewhere; otherwise your portal will drift out of sync when customers disconnect through my.plaid.com or the application itself instead of through you.`}</p>
      <p><strong parentName="p">{`When it fires:`}</strong>{` After a customer or application revokes authorization.`}</p>
      <p><strong parentName="p">{`Payload includes:`}</strong>{` The customer (`}<inlineCode parentName="p">{`user_identifier`}</inlineCode>{`), application details, and who initiated the revocation (`}<inlineCode parentName="p">{`initiator`}</inlineCode>{`: `}<inlineCode parentName="p">{`DATA_ACCESS_PLATFORM`}</inlineCode>{`, `}<inlineCode parentName="p">{`INDIVIDUAL`}</inlineCode>{`, or `}<inlineCode parentName="p">{`DATA_RECIPIENT`}</inlineCode>{`).`}</p>
      <p><strong parentName="p">{`What to do:`}</strong>{` Mark the connection revoked in your own records and update your portal. Revocations that happen in Plaid Portal or in the application itself also land in `}<a parentName="p" {...{
          "href": "#read-consent-activity-and-last-access-times"
        }}><inlineCode parentName="a">{`/item/activity/list`}</inlineCode></a>{` as `}<inlineCode parentName="p">{`PORTAL_UNLINK`}</inlineCode>{` and `}<inlineCode parentName="p">{`ITEM_REMOVE`}</inlineCode>{` entries.`}</p>
      <h2 {...{
        "id": "best-practices"
      }}>{`Best practices`}</h2>
      <ul>
        <li parentName="ul">{`Call `}<a parentName="li" {...{
            "href": "#revoke-an-applications-access"
          }}><inlineCode parentName="a">{`/item/application/unlink`}</inlineCode></a>{` as soon as a customer disconnects an application, to keep the ecosystem in sync during revocations`}</li>
        <li parentName="ul">{`Implement the `}<inlineCode parentName="li">{`CONSENT_REVOKED`}</inlineCode>{` webhook if you offer a consent portal`}</li>
        <li parentName="ul">{`Set refresh token expiration to 13+ months (allows buffer for reauthorization)`}</li>
        <li parentName="ul">{`Direct users to `}<a parentName="li" {...{
            "href": "https://my.plaid.com"
          }}>{`my.plaid.com`}</a>{` for self-service connection management`}</li>
      </ul>
      <h2 {...{
        "id": "testing-in-sandbox"
      }}>{`Testing in Sandbox`}</h2>
      <p>{`Point the same calls at `}<inlineCode parentName="p">{`sandbox.plaid.com`}</inlineCode>{` with your Sandbox secret. `}<a parentName="p" {...{
          "href": "#exchange-a-customer-identifier-for-an-access-token"
        }}><inlineCode parentName="a">{`/item/import`}</inlineCode></a>{` returns a working Sandbox access token, and `}<a parentName="p" {...{
          "href": "#list-a-customers-connected-applications"
        }}><inlineCode parentName="a">{`/item/application/list`}</inlineCode></a>{` and `}<a parentName="p" {...{
          "href": "#read-consent-activity-and-last-access-times"
        }}><inlineCode parentName="a">{`/item/activity/list`}</inlineCode></a>{` return fixture data for demo applications, so you can build and test your portal's rendering before you have live connections.`}</p>
      <p>{`That fixture data is static: unlinking a demo application does not change what `}<a parentName="p" {...{
          "href": "#list-a-customers-connected-applications"
        }}><inlineCode parentName="a">{`/item/application/list`}</inlineCode></a>{` returns. To exercise the state changes instead, use dynamic Sandbox, which is enabled for most clients: connect a demo app, see it appear in `}<a parentName="p" {...{
          "href": "#list-a-customers-connected-applications"
        }}><inlineCode parentName="a">{`/item/application/list`}</inlineCode></a>{`, unlink it, and see it move to `}<inlineCode parentName="p">{`disconnected_applications`}</inlineCode>{`. `}<inlineCode parentName="p">{`/sandbox/item/application/seed`}</inlineCode>{` connects a demo application to a customer, standing in for a customer completing your OAuth flow. If it returns `}<inlineCode parentName="p">{`403 SANDBOX_SEEDING_NOT_ENABLED`}</inlineCode>{`, dynamic Sandbox is off for your client — ask your Plaid solutions engineering team to enable it. `}<a parentName="p" {...{
          "href": "#read-consent-activity-and-last-access-times"
        }}><inlineCode parentName="a">{`/item/activity/list`}</inlineCode></a>{` always serves its fixture in Sandbox, dynamic Sandbox or not, so you can build your portal's activity view against it, but you won't see real activity until Production.`}</p>
      <h4 {...{
        "id": "post-sandboxitemapplicationseed"
      }}><inlineCode parentName="h4">{`POST /sandbox/item/application/seed`}</inlineCode></h4>
      <Schema method='POST' route='/sandbox/item/application/seed' type='request' mdxType="Schema">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST 'https://sandbox.plaid.com/sandbox/item/application/seed' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": "<client_id>",
    "secret": "<sandbox_secret>",
    "access_token": "<access_token>",
    "application_id": "c048eab9-a70a-42fe-94e9-35980d0a965c"
  }'
`}</code></pre>
      </Schema>
      <Schema method='POST' route='/sandbox/item/application/seed' type='response' mdxType="Schema" />
      <p>{`Plaid provides three demo applications to connect:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Demo application`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`application_id`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Budget Works`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`c048eab9-a70a-42fe-94e9-35980d0a965c`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Incremental Investing`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`36b78a27-ebbb-417b-a7b7-351734d2f8bd`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`My Peer Payer`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`3f952a96-b6f9-4f09-a1f7-1a474cb128f6`}</inlineCode></td>
          </tr>
        </tbody>
      </table>
      <h2 {...{
        "id": "errors"
      }}>{`Errors`}</h2>
      <p>{`These endpoints return Plaid's standard `}<a parentName="p" {...{
          "href": "/docs/errors/"
        }}>{`error object`}</a>{` (`}<inlineCode parentName="p">{`error_type`}</inlineCode>{`, `}<inlineCode parentName="p">{`error_code`}</inlineCode>{`, `}<inlineCode parentName="p">{`error_message`}</inlineCode>{`, `}<inlineCode parentName="p">{`display_message`}</inlineCode>{`, `}<inlineCode parentName="p">{`request_id`}</inlineCode>{`). Log `}<inlineCode parentName="p">{`request_id`}</inlineCode>{` when you encounter an error to speed up troubleshooting with Plaid.`}</p>
      <Layout.FullWidth>
        <div className="status-error-code-table">
          <table>
            <thead parentName="table">
              <tr parentName="thead">
                <th parentName="tr" {...{
                  "align": null
                }}>{`Status`}</th>
                <th parentName="tr" {...{
                  "align": null
                }}>{`error_code`}</th>
                <th parentName="tr" {...{
                  "align": null
                }}>{`Returned when`}</th>
              </tr>
            </thead>
            <tbody parentName="table">
              <tr parentName="tbody">
                <td parentName="tr" {...{
                  "align": null
                }}>{`400`}</td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`MISSING_FIELDS`}</inlineCode></td>
                <td parentName="tr" {...{
                  "align": null
                }}>{`A required field is absent from the request body`}</td>
              </tr>
              <tr parentName="tbody">
                <td parentName="tr" {...{
                  "align": null
                }}>{`400`}</td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`INVALID_USER_AUTH`}</inlineCode></td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`user_auth.user_id`}</inlineCode>{` is missing or is not a non-empty string`}</td>
              </tr>
              <tr parentName="tbody">
                <td parentName="tr" {...{
                  "align": null
                }}>{`400`}</td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`ITEM_NOT_CREATED_BY_ITEM_IMPORT`}</inlineCode></td>
                <td parentName="tr" {...{
                  "align": null
                }}>{`The `}<inlineCode parentName="td">{`access_token`}</inlineCode>{` belongs to an Item created some other way than `}<a parentName="td" {...{
                    "href": "#exchange-a-customer-identifier-for-an-access-token"
                  }}><inlineCode parentName="a">{`/item/import`}</inlineCode></a></td>
              </tr>
              <tr parentName="tbody">
                <td parentName="tr" {...{
                  "align": null
                }}>{`400`}</td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`ITEM_IMPORT_INACTIVE_ACCESS_TOKEN`}</inlineCode></td>
                <td parentName="tr" {...{
                  "align": null
                }}>{`The `}<inlineCode parentName="td">{`access_token`}</inlineCode>{` is no longer active`}</td>
              </tr>
              <tr parentName="tbody">
                <td parentName="tr" {...{
                  "align": null
                }}>{`400`}</td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`FI_PARTNER_INSTITUTION_NOT_SET_UP`}</inlineCode></td>
                <td parentName="tr" {...{
                  "align": null
                }}>{`Your institution is not configured for Permissions Manager — contact your Plaid solutions engineering team`}</td>
              </tr>
              <tr parentName="tbody">
                <td parentName="tr" {...{
                  "align": null
                }}>{`404`}</td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`CONNECTED_APPLICATION_NOT_FOUND`}</inlineCode></td>
                <td parentName="tr" {...{
                  "align": null
                }}>{`The `}<inlineCode parentName="td">{`application_id`}</inlineCode>{` and `}<inlineCode parentName="td">{`access_token`}</inlineCode>{` pair has no active connection`}</td>
              </tr>
              <tr parentName="tbody">
                <td parentName="tr" {...{
                  "align": null
                }}>{`404`}</td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`NO_CONNECTED_APPLICATIONS`}</inlineCode></td>
                <td parentName="tr" {...{
                  "align": null
                }}>{`The customer has no connections, for the older integrations that receive a `}<inlineCode parentName="td">{`404`}</inlineCode>{` for this rather than a `}<inlineCode parentName="td">{`200`}</inlineCode></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Layout.FullWidth>
      <p>{`A customer with no connections is a normal state, not a failure: `}<a parentName="p" {...{
          "href": "#list-a-customers-connected-applications"
        }}><inlineCode parentName="a">{`/item/application/list`}</inlineCode></a>{` returns `}<inlineCode parentName="p">{`200`}</inlineCode>{` with an empty `}<inlineCode parentName="p">{`applications`}</inlineCode>{` array. If you integrated before February 2022, you may receive `}<inlineCode parentName="p">{`404 NO_CONNECTED_APPLICATIONS`}</inlineCode>{` instead; render either as an empty portal.`}</p>
      <p>{`Unlinking an application whose access has already ended returns `}<inlineCode parentName="p">{`204`}</inlineCode>{` with `}<inlineCode parentName="p">{`APPLICATION_ALREADY_UNLINKED`}</inlineCode>{`. Treat it as success — the revocation you asked for is already in effect — which makes retries safe.`}</p>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 2,
        title: "Overview",
      },
{
        id: "authentication",
        level: 2,
        title: "Authentication",
      },
{
        id: "customer-identifiers-and-access-tokens",
        level: 2,
        title: "Customer identifiers and access tokens",
      },
{
        id: "exchange-a-customer-identifier-for-an-access-token",
        level: 3,
        title: "Exchange a customer identifier for an access token",
      },
{
        id: "post-itemimport",
        level: 4,
        title: "POST /item/import",
      },
{
        id: "endpoints",
        level: 2,
        title: "Endpoints",
      },
{
        id: "list-a-customers-connected-applications",
        level: 3,
        title: "List a customer's connected applications",
      },
{
        id: "post-itemapplicationlist",
        level: 4,
        title: "POST /item/application/list",
      },
{
        id: "reading-the-scopes-object",
        level: 4,
        title: "Reading the scopes object",
      },
{
        id: "read-consent-activity-and-last-access-times",
        level: 3,
        title: "Read consent activity and last access times",
      },
{
        id: "post-itemactivitylist",
        level: 4,
        title: "POST /item/activity/list",
      },
{
        id: "revoke-an-applications-access",
        level: 3,
        title: "Revoke an application's access",
      },
{
        id: "post-itemapplicationunlink",
        level: 4,
        title: "POST /item/application/unlink",
      },
{
        id: "webhooks",
        level: 2,
        title: "Webhooks",
      },
{
        id: "authorization_granted",
        level: 3,
        title: "AUTHORIZATION_GRANTED",
      },
{
        id: "consent_revoked",
        level: 3,
        title: "CONSENT_REVOKED",
      },
{
        id: "best-practices",
        level: 2,
        title: "Best practices",
      },
{
        id: "testing-in-sandbox",
        level: 2,
        title: "Testing in Sandbox",
      },
{
        id: "post-sandboxitemapplicationseed",
        level: 4,
        title: "POST /sandbox/item/application/seed",
      },
{
        id: "errors",
        level: 2,
        title: "Errors",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
